import React, { memo, useCallback, useRef, useState } from 'react'

import Dropzone from 'react-dropzone'

import axios from 'axios'
import { func } from 'prop-types'

import useRequireLogin from 'Pods/ThreeDStudio/Hooks/useRequireLogin'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import { NOOP } from 'Utils/stableDefaults'

import CurrentModelCard from './Components/CurrentModelCard'
import ReferenceDropzone from './Components/ReferenceDropzone'
import ReferenceGrid from './Components/ReferenceGrid'

const UPLOAD_URL = '/job_attachments'
// Mirrors the legacy FileUpload client-side limit for /job_attachments
const MAX_FILE_SIZE = 100 * 1024 * 1024
const UNTITLED_MODEL = 'Untitled model'

const flashError = message => App.events.publish('app:notification:flash:error', message)

function VisualReferences({ onChange = NOOP }) {
  const [references, setReferences] = useState([])
  const modelTitle = useThreeDStudioStore(state => state.ui?.openedModel?.title ?? state.ui?.openedModel?.prompt)
  const modelThumbUrl = useThreeDStudioStore(state => state.ui?.openedModel?.outputAssets?.[0]?.previewUrl)
  const dropzoneRef = useRef(null)
  const referencesRef = useRef(references)
  const nextIdRef = useRef(0)
  // Attaching a reference requires an account; prompt a logged-out user to sign up.
  const requireLogin = useRequireLogin()

  // Single mutation path: keep referencesRef in sync synchronously so concurrent uploads
  // always read the latest list, and never call the parent's onChange during render.
  const commit = useCallback((next) => {
    referencesRef.current = next
    setReferences(next)
  }, [])

  const emitChange = useCallback((list) => {
    onChange(list.filter(reference => reference.isUploaded).map(reference => reference.attachmentId))
  }, [onChange])

  const openDialog = useCallback(() => {
    if (!requireLogin()) {
      return
    }

    if (dropzoneRef.current) {
      dropzoneRef.current.open()
    }
  }, [requireLogin])

  const uploadFile = useCallback((file) => {
    const data = new FormData()

    data.append('file', file)

    axios.post(UPLOAD_URL, data, {
      onUploadProgress: (event) => {
        const progress = Math.round((event.loaded * 100) / event.total)

        commit(referencesRef.current.map(
          reference => (reference.name === file.name ? { ...reference, progress } : reference),
        ))
      },
    })
      .then((response) => {
        const attachment = response.data[0]

        if (!referencesRef.current.some(reference => reference.name === file.name)) {
          axios.delete(attachment.delete_url).catch(() => {})
          return
        }

        const next = referencesRef.current.map(reference => (reference.name === file.name
          ? {
            ...reference,
            attachmentId: attachment.id,
            thumbUrl: attachment.thumb_url,
            deleteUrl: attachment.delete_url,
            progress: 100,
            isUploaded: true,
          }
          : reference))

        commit(next)
        emitChange(next)
      })
      .catch((error) => {
        const message = error.response?.data?.errors?.join(', ')
          || 'Your file failed to attach. Please try again or contact our support'

        flashError(message)

        const next = referencesRef.current.filter(reference => reference.name !== file.name)

        commit(next)
        emitChange(next)
      })
  }, [commit, emitChange])

  const handleDrop = useCallback((files) => {
    if (!files.length) {
      return
    }

    if (!requireLogin()) {
      return
    }

    const existing = referencesRef.current
    const accepted = []

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        flashError('Only image files can be attached.')
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        flashError('Attachment is too big, please try to not exceed 100MBs.')
        return
      }

      const isDuplicate = existing.some(reference => reference.name === file.name)
        || accepted.some(item => item.name === file.name)

      if (isDuplicate) {
        flashError('You cannot attach file with the same filename')
        return
      }

      accepted.push(file)
    })

    if (!accepted.length) return

    const baseId = nextIdRef.current

    nextIdRef.current += accepted.length

    commit([
      ...referencesRef.current,
      ...accepted.map((file, index) => ({
        id: `reference-${baseId + index}`,
        name: file.name,
        isImage: file.type.startsWith('image/'),
        progress: 0,
        isUploaded: false,
        attachmentId: null,
        thumbUrl: null,
        deleteUrl: null,
      })),
    ])

    accepted.forEach(uploadFile)
  }, [commit, uploadFile, requireLogin])

  const handleRemove = useCallback((name) => {
    const removed = referencesRef.current.find(reference => reference.name === name)

    if (removed?.deleteUrl) axios.delete(removed.deleteUrl).catch(() => {})

    const next = referencesRef.current.filter(reference => reference.name !== name)

    commit(next)
    emitChange(next)
  }, [commit, emitChange])

  const hasReferences = references.length > 0
  const hasModel = !!(modelTitle || modelThumbUrl)
  const displayTitle = modelTitle?.trim() || UNTITLED_MODEL

  return (
    <div className="visual-references">
      <span className="visual-references__label cgt-body-base-semibold">Visual references</span>
      <div className="visual-references__box">
        <div className="visual-references__note">
          <p className="cgt-caption-semibold">We&apos;ll share the inputs used. You can also add references.</p>
        </div>
        {hasModel ? <CurrentModelCard thumbUrl={modelThumbUrl} title={displayTitle} /> : null}
        <Dropzone
          ref={dropzoneRef}
          className="visual-references__drop"
          activeClassName="visual-references__drop--active"
          accept="image/*"
          onDrop={handleDrop}
          disableClick
          disablePreview
          name="assist-references-input"
        >
          {hasReferences
            ? <ReferenceGrid references={references} onAdd={openDialog} onRemove={handleRemove} />
            : <ReferenceDropzone onBrowse={openDialog} />}
        </Dropzone>
      </div>
    </div>
  )
}

VisualReferences.propTypes = {
  onChange: func,
}

export default memo(VisualReferences)
